import type { MessageKey, Role } from '@ralph/contracts'
import { formatCount, formatList, pluralCategory } from '@/i18n/format.ts'
import type { ServerMessages } from '@/i18n/messages/en/errors/index.ts'

/**
 * The locale this file IS — see the note in `sharing.ts`, which says it first.
 *
 * An entry here takes its parameters and nothing else, because it is looked up by key and
 * called with no formatter bound, so the one entry that counts something names its own locale.
 */
const LOCALE = 'en'

/**
 * Every `notifications.*` key, without restating the list.
 *
 * The sibling files in this directory spell out their keys in a `Pick<…>` union. This one is
 * the whole prefix and nothing else, so the prefix is a better list than a copy of it would
 * be: a key added to `contracts/messages/notifications.ts` and not written here fails `tsc`
 * on THIS file, naming the key, instead of failing further away on the aggregate.
 */
type NotificationKey = Extract<MessageKey, `notifications.${string}`>

/** Who an audience is, in the words D-37 bound. Never assembled at a call site (D-20). */
const audienceOf = (audience: 'everyone' | 'groups', groups: string[]): string =>
  audience === 'everyone' ? 'everyone who signs in' : formatList(LOCALE, groups)

/**
 * The four roles as they appear INSIDE a sentence.
 *
 * Written here rather than reached for in `people.ts`: an entry in this map receives its
 * PARAMETERS and nothing else — there is no catalogue in scope to read a sibling namespace
 * from — and `Record<Role, string>` makes a fifth role a compile error in both places rather
 * than a raw token in a notification (LOC-018).
 */
const ROLE_IN_SENTENCE: Record<Role, string> = {
  user: 'standard',
  auditor: 'auditor',
  operator: 'operator',
  admin: 'administrator'
}

/**
 * What a notification says, in English (the source of truth AND the type).
 *
 * These are not refusals, and they sit in the `errors` map because that map is not really
 * about errors: it is every sentence the SERVER names and this client renders (LOC-032). A
 * notification is the purest case of it — the row carries `subject_key` and `body_key`, and
 * the words are chosen when somebody opens the inbox, in the language they are reading in
 * then rather than the one whoever triggered it happened to be using (D-5 amended, F-26).
 *
 * Every sentence here has an English twin already stored on the row, because `subject` and
 * `body` stay permanently: they are the operator's record and they are what a row written
 * before `0014` renders from. The two are kept saying the same thing on purpose — if a
 * sentence is improved, it is improved in both places.
 */
export const notifications: Pick<ServerMessages, NotificationKey> = {
  // --- a sharing request, decided -------------------------------------------

  'notifications.sharing_decided_subject': ({ name, outcome, audience, groups }) =>
    outcome === 'approved'
      ? `“${name}” is now shared with ${audienceOf(audience, groups)}`
      : `“${name}” was not shared with ${audienceOf(audience, groups)}`,
  'notifications.sharing_approved_body': () =>
    'It is live for them now, and later versions reach them without another review.',
  'notifications.sharing_declined_body': ({ admin }) => `${admin} declined it.`,
  'notifications.sharing_decided_note_body': ({ admin, outcome, note }) =>
    outcome === 'approved' ? `${admin} approved it: ${note}` : `${admin} declined it: ${note}`,

  // --- a sharing request that ended without a decision ----------------------

  'notifications.sharing_request_closed_subject': ({ name }) => `Your request to share “${name}” was closed`,
  // `satisfies` rather than a lookup with a fallback: a fifth closing reason added to the
  // contract should fail the build here, not render a blank where the cause was (LOC-018).
  'notifications.sharing_closed_body': ({ reason }) =>
    (
      ({
        group_deleted: 'A group it named was deleted, so there was nothing left to approve. Nothing changed.',
        requester_lost_authority:
          'It was closed because the person who asked no longer maintains it. Nothing changed; someone who does can ask again.',
        item_unavailable: 'It was closed because it can no longer be shared as it was asked. Nothing changed.',
        migrated: 'Groups are now managed here, and the groups it named no longer exist. Nothing changed.'
      }) satisfies Record<typeof reason, string>
    )[reason],
  'notifications.sharing_closed_suspended_body': () =>
    'An administrator suspended the agent, so there was nothing left to share. Nothing changed.',
  'notifications.sharing_closed_taken_down_body': ({ actor }) =>
    `${actor} took the agent down, so there was nothing left to share. Nothing changed.`,
  'notifications.sharing_closed_base_deleted_body': ({ actor }) =>
    `${actor} deleted the knowledge base, so there was nothing left to share.`,
  'notifications.sharing_closed_group_deleted_subject': ({ group }) =>
    `Your sharing request was closed: “${group}” was deleted`,
  'notifications.sharing_closed_group_deleted_body': () =>
    'Nothing changed. Choose who can use it again if you still want to share it.',

  'notifications.sharing_request_withdrawn_subject': ({ name }) => `Your request to share “${name}” was withdrawn`,
  'notifications.sharing_request_withdrawn_body': ({ actor }) =>
    `${actor} withdrew it. Nothing changed about who can use it.`,
  'notifications.sharing_request_replaced_subject': ({ name }) => `Your request to share “${name}” was replaced`,
  'notifications.sharing_request_replaced_body': ({ actor }) =>
    `${actor} asked for something else before an administrator decided. That request is the one waiting now.`,

  // --- asking to share ------------------------------------------------------

  'notifications.sharing_requested_subject': ({ actor, name }) => `${actor} asked to share “${name}”`,
  'notifications.sharing_requested_body': ({ item, audience, groups }) =>
    item === 'agent'
      ? `They asked to share this agent with ${audienceOf(audience, groups)}.`
      : `They asked to share this knowledge base with ${audienceOf(audience, groups)}.`,

  // --- shared into a group --------------------------------------------------

  'notifications.shared_into_group_subject': ({ name, group }) => `“${name}” is now shared with ${group}`,
  'notifications.shared_into_group_body': ({ actor, group, item }) =>
    item === 'agent'
      ? `${actor} shared this agent with ${group}. You get this because you manage ${group}.`
      : `${actor} shared this knowledge base with ${group}. You get this because you manage ${group}.`,

  // --- lending a base to an agent -------------------------------------------

  'notifications.lending_requested_subject': ({ asker, agent, base }) =>
    `${asker} asked to let “${agent}” answer from “${base}”`,
  'notifications.lending_requested_body': ({ agent, base }) =>
    `If you allow it, everyone who can use “${agent}” gets answers from “${base}” through it.`,
  'notifications.lending_allowed_subject': ({ agent, base }) => `Allowed: “${agent}” now answers from “${base}”`,
  'notifications.lending_allowed_body': ({ decider, agent }) =>
    `${decider} allowed it. Everyone who can use “${agent}” gets answers from it through that agent.`,
  'notifications.lending_declined_subject': ({ agent, base }) => `Declined: “${agent}” will not answer from “${base}”`,
  'notifications.lending_declined_body': ({ decider }) => `${decider} said no.`,
  'notifications.lending_decided_note_body': ({ decider, note }) => `${decider}: “${note}”`,
  'notifications.lending_closed_base_deleted_subject': ({ base }) => `Closed: “${base}” was deleted`,
  'notifications.lending_closed_base_deleted_body': ({ actor, base, agent }) =>
    `${actor} deleted the knowledge base “${base}”, so “${agent}” cannot answer from it. Your request was closed.`,
  'notifications.lending_closed_group_deleted_subject': ({ group }) => `Closed: “${group}” was deleted`,
  'notifications.lending_closed_group_deleted_body': ({ actor, base, agent }) =>
    `${actor} deleted the group that maintained “${base}”, so your request for “${agent}” to answer from it was ` +
    'closed. Ask its new maintainers if you still need it.',

  // --- an agent's health ----------------------------------------------------

  'notifications.agent_needs_attention_subject': () => 'One of your agents needs attention',
  // `Intl.PluralRules`, never `count === 1` — the same check is wrong at zero in the other
  // shipped language, which is why it is asked here too (D-19).
  'notifications.agent_needs_attention_body': ({ count }) =>
    pluralCategory(LOCALE, count) === 'one'
      ? `${formatCount(LOCALE, count)} thing needs attention. Open the agent to see what changed.`
      : `${formatCount(LOCALE, count)} things need attention. Open the agent to see what changed.`,
  'notifications.agent_lost_base_subject': ({ agent }) => `“${agent}” lost one of its knowledge bases`,
  'notifications.agent_lost_base_body': ({ actor, base, agent }) =>
    `${actor} deleted the knowledge base “${base}”. “${agent}” keeps working, but it can no longer answer from ` +
    'that material. Open the builder to attach something else.',

  // --- the listing ----------------------------------------------------------

  'notifications.agent_suspended_subject': ({ name }) => `“${name}” has been suspended`,
  'notifications.ownership_transferred_subject': ({ name, owner }) => `“${name}” now belongs to ${owner}`,
  'notifications.ownership_transferred_body': () =>
    'Whoever owns an agent is accountable for what it says and does. Who can use it did not change.',
  'notifications.ownership_transferred_badge_body': () =>
    'Its official badge was removed with the move. An administrator can grant it again after checking the agent.',
  'notifications.editor_invited_subject': ({ actor, agent }) => `${actor} asked you to help build “${agent}”`,
  'notifications.editor_invited_body': () =>
    'You can accept or decline from your agents list. Nothing changes until you do.',
  'notifications.question_shared_subject': ({ agent }) => `Somebody shared a question with “${agent}”`,
  'notifications.question_shared_body': () =>
    'Open the agent’s activity to see what people asked that it could not answer well.',

  // --- what happens to a person's own access --------------------------------

  'notifications.role_granted_subject': ({ actor, role }) => `${actor} gave you the ${ROLE_IN_SENTENCE[role]} role`,
  'notifications.role_denied_subject': ({ actor, role }) => `${actor} removed your ${ROLE_IN_SENTENCE[role]} access`,
  'notifications.role_revoked_subject': ({ actor, role }) => `${actor} removed your ${ROLE_IN_SENTENCE[role]} role`,
  'notifications.role_restored_subject': ({ actor, role }) => `${actor} restored your ${ROLE_IN_SENTENCE[role]} access`,
  'notifications.role_approved_subject': ({ actor }) => `Your new access was approved by ${actor}`,
  'notifications.role_approved_body': () =>
    'A second administrator has to approve an administrator grant on this deployment.',
  'notifications.role_changed_body': () => 'Ask an administrator if you are not sure what this changes.',
  'notifications.role_revoked_body': () => 'Ask an administrator if you need it back.',
  'notifications.role_in_effect_body': () => 'It is already in effect.',

  // --- shared by several of the above ---------------------------------------

  'notifications.their_note_body': ({ note }) => `Their note: “${note}”`
}
